/**
 * Situational Awareness Layer — Helper: Outcome Capture
 * ─────────────────────────────────────────────────────
 * Pure helpers that derive next-step + lifecycle defaults from an
 * AppointmentResult + optional structured objections.
 *
 * RULE (mem://architecture/situational-awareness-layer):
 * Do NOT inline `if (result.resultType === ...)` branches in modals or
 * panels. Extend these helpers instead.
 *
 * Phase 1 scope: derivation only. Phase 2 will wire the derived values
 * into Universal Sequence + Manager Queue + Marketing Attribution.
 */
import type { AppointmentResult, AppointmentResultType } from "../appointmentResultTypes";
import type {
  CloseReason,
  CommunicationStatus,
  FollowUpType,
  ObjectionCode,
  OutcomeLifecycle,
  OutcomeNextStep,
  OutcomeObjections,
  ProposalSnapshot,
} from "../appointmentOutcomeTypes";

// ────────────────────────────────────────────────────────────────────
// Next-step derivation
// ────────────────────────────────────────────────────────────────────

/**
 * Default follow-up type per result + primary objection. Operators can
 * override in the capture modal, but the suggested default is always
 * derived here (single source of truth).
 */
export function deriveFollowUpType(
  result: Pick<AppointmentResult, "resultType">,
  primaryObjection?: ObjectionCode,
): FollowUpType {
  if (result.resultType === "sold") return "no_action";
  if (result.resultType === "rescheduled") return "sales_rep_followup";
  if (result.resultType === "no_demo") return "sales_rep_followup";

  // not-sold path — branch on objection
  switch (primaryObjection) {
    case "price":
      return "pricing_review";
    case "financing":
      return "financing_followup";
    case "spouse_not_present":
    case "not_decision_maker":
      return "sales_rep_followup";
    case "trust":
    case "comparing_competitors":
      return "manager_call";
    case "timing":
      return "nurture_sequence";
    default:
      return "sales_rep_followup";
  }
}

/**
 * Days-from-now for the suggested follow-up due date. Tight loops on
 * decision-maker absence; longer for nurture.
 */
export function deriveFollowUpDueOffsetDays(followUpType: FollowUpType): number {
  switch (followUpType) {
    case "no_action": return 0;
    case "manager_call": return 1;
    case "pricing_review": return 1;
    case "financing_followup": return 2;
    case "sales_rep_followup": return 2;
    case "nurture_sequence": return 7;
  }
}

export function deriveManagerReview(
  result: Pick<AppointmentResult, "resultType" | "pricingSnapshot">,
  proposal?: ProposalSnapshot,
  objections?: OutcomeObjections,
): boolean {
  // Manager review whenever: discount applied above threshold, primary
  // objection signals trust/competitor (manager call helps), or high-value
  // not_sold deal walking out the door.
  if (proposal && proposal.discountAmount > 0) return true;
  if (objections?.primary === "trust" || objections?.primary === "comparing_competitors") {
    return true;
  }
  if (result.resultType === "no_sale") {
    const value = proposal?.finalPrice
      ?? result.pricingSnapshot?.bestValuePrice
      ?? result.pricingSnapshot?.standardPrice
      ?? 0;
    if (value >= 8000) return true;
  }
  return false;
}

export function deriveDiscountFlag(proposal?: ProposalSnapshot): boolean {
  if (!proposal) return false;
  if (proposal.discountAmount <= 0) return false;
  // Flag any discount ≥10% of base price OR ≥$500 absolute.
  const pct = proposal.basePrice > 0 ? proposal.discountAmount / proposal.basePrice : 0;
  return pct >= 0.10 || proposal.discountAmount >= 500;
}

export function deriveNextStep(
  result: Pick<AppointmentResult, "resultType" | "pricingSnapshot">,
  objections?: OutcomeObjections,
  proposal?: ProposalSnapshot,
): OutcomeNextStep {
  const followUpType = deriveFollowUpType(result, objections?.primary);
  const followUpRequired = followUpType !== "no_action";
  const offsetDays = deriveFollowUpDueOffsetDays(followUpType);
  const followUpDueAt = followUpRequired
    ? new Date(Date.now() + offsetDays * 24 * 60 * 60 * 1000).toISOString()
    : undefined;
  return {
    followUpRequired,
    followUpType,
    followUpDueAt,
    managerReviewRequired: deriveManagerReview(result, proposal, objections),
    discountApprovalFlag: deriveDiscountFlag(proposal),
  };
}

// ────────────────────────────────────────────────────────────────────
// Lifecycle derivation
// ────────────────────────────────────────────────────────────────────

/**
 * Default lifecycle. Critical rule (per spec): opportunity stays ACTIVE
 * until explicitly resolved. Only `sold` and `not_fit`-type objections
 * close the loop automatically.
 */
export function deriveLifecycle(
  resultType: AppointmentResultType,
  primaryObjection?: ObjectionCode,
): OutcomeLifecycle {
  if (resultType === "sold") {
    return { isClosed: true, closeReason: "won", communicationStatus: "active" };
  }
  if (resultType === "no_sale" && primaryObjection === "product_fit") {
    return { isClosed: true, closeReason: "not_fit", communicationStatus: "active" };
  }
  // Default: stay open. Comms remain active so nurture sequences can run
  // (Phase 2 will gate on this field).
  return { isClosed: false, communicationStatus: "active" };
}

// ────────────────────────────────────────────────────────────────────
// Display tone for lifecycle/comms badges
// ────────────────────────────────────────────────────────────────────

export function lifecycleToneClasses(lifecycle: OutcomeLifecycle): {
  container: string;
  text: string;
  dot: string;
  label: string;
} {
  if (lifecycle.isClosed) {
    if (lifecycle.closeReason === "won") {
      return {
        container: "border-success/40 bg-success/5",
        text: "text-success",
        dot: "bg-success",
        label: "Closed — Won",
      };
    }
    return {
      container: "border-muted bg-muted/30",
      text: "text-muted-foreground",
      dot: "bg-muted-foreground/50",
      label: "Closed",
    };
  }
  return {
    container: "border-primary/30 bg-primary/5",
    text: "text-primary",
    dot: "bg-primary",
    label: "Active",
  };
}

export function commsStatusToneClasses(status: CommunicationStatus): {
  container: string;
  text: string;
  dot: string;
} {
  switch (status) {
    case "active":
      return { container: "border-primary/30 bg-primary/5", text: "text-primary", dot: "bg-primary" };
    case "paused":
      return { container: "border-warning/30 bg-warning/5", text: "text-warning", dot: "bg-warning" };
    case "unsubscribed":
      return { container: "border-destructive/40 bg-destructive/5", text: "text-destructive", dot: "bg-destructive" };
  }
}

// Re-export for callers that want a single import surface.
export type { CloseReason };
